import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { ordersAssignedCountsFromAlgolia } from "../../../../api/postCall/ordersAssignedCounts";
import ToolTipUsingPortal from "../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import { AlgoliaFacetCountsResponseModel } from "../../../../data-model/algoliaFacetCountsResponseModel";
import { InfoIcon } from "../../../../svg/info-icon";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { capitalizeString } from "../../../../utils/stringModifications";

const OrderAssignmentInfo = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="order-assignment-info--container mr-2 flex gap-1">
      <div className="order-assignment-info--text">
        View Order Assignment Information
      </div>
      <div
        className="order-assignment-info--icon"
        ref={parentRef}
        onMouseEnter={() => setShowTooltip(true)}
      >
        <InfoIcon />
        {showTooltip && (
          <ToolTipUsingPortal
            title="Order Assignment Information"
            placement="bottom"
            showTooltip={showTooltip}
            parentRef={parentRef}
            offsetPx={10}
            children={
              <div onMouseLeave={() => setShowTooltip(false)}>
                <OrderAssignmentInfoContent />
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default OrderAssignmentInfo;

interface OrderAssignmentInfoContentProps {
  data: {
    loading: boolean;
    error: string | null;
    response: AlgoliaFacetCountsResponseModel | null;
  };
}

const OrderAssignmentInfoContent = () => {
  const [apiResponse, setApiResponse] =
    useState<OrderAssignmentInfoContentProps>({
      data: {
        loading: false,
        error: null,
        response: null,
      },
    });

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchApiResponse = async () => {
      try {
        setApiResponse({
          data: { loading: true, error: null, response: null },
        });

        const response = await ordersAssignedCountsFromAlgolia(
          {
            org_id: getOrgIdForFetchExternalWorklist(),
            facet_attribute: "assigned_to_name",
            created_at_start: moment().format("YYYY-MM-DD"),
            created_at_end: moment().format("YYYY-MM-DD"),
          },
          controller.signal,
        );

        if (!isMounted) return;

        setApiResponse({
          data: { loading: false, error: null, response },
        });
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Request aborted (ignored in dev mode)");
          return;
        }

        if (!isMounted) return;

        setApiResponse({
          data: { loading: false, error: error.message, response: null },
        });
        console.error(error);
      }
    };

    fetchApiResponse();

    return () => {
      isMounted = false;
      controller.abort("Component unmounted.");
    };
  }, []);

  if (apiResponse.data.loading) {
    return (
      <div className="flex items-center gap-2">
        <SpinningLoader /> Loading...
      </div>
    );
  }
  if (apiResponse.data.error) {
    return <div>Error</div>;
  }
  return (
    <div className="order-assignment-info-content--container flex max-h-[30rem] w-40 flex-col gap-2 overflow-y-auto">
      <div className="order-assignment-count flex justify-between gap-2 font-bold">
        <div className="assignee-name">Total Orders</div>
        <div className="assignee-count">
          {Object.values(
            apiResponse.data.response?.facet_results?.["assigned_to_name"] ||
              {},
          ).reduce((acc, curr) => acc + curr, 0)}
        </div>
      </div>
      {Object.entries(
        apiResponse.data.response?.facet_results?.["assigned_to_name"] || {},
      ).map(([key, value]) => (
        <div
          className="order-assignment-count flex justify-between gap-2"
          key={key}
        >
          <div className="assignee-name">{capitalizeString(key)}</div>
          <div className="assignee-count">{value}</div>
        </div>
      ))}
    </div>
  );
};
